import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetActiveDepartmentsQuery } from '../redux/employeeApi';
import { authFetch } from '../utils/authFetch';
import { departmentImageSrc } from '../utils/departmentImages';

const getFileNameFromUrl = (url) => {
  if (!url) return '';
  try {
    const { pathname } = new URL(url);
    const last = pathname.split('/').filter(Boolean).pop() || '';
    return decodeURIComponent(last);
  } catch {
    const parts = String(url).split('?')[0].split('/').filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || '');
  }
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const getExtensionFromUrl = (url) => {
  if (!url) return '';
  const noQuery = String(url).split('?')[0];
  const last = noQuery.split('/').filter(Boolean).pop() || '';
  const dotIdx = last.lastIndexOf('.');
  return dotIdx >= 0 ? last.slice(dotIdx + 1).toLowerCase() : '';
};

const downloadFile = async (url, fileName) => {
  if (!url) return;
  try {
    const res = await authFetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    // Fallback when CORS blocks fetch or server disallows it.
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'download';
    a.rel = 'noreferrer noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

const getPreviewUrl = (url) => {
  if (!url) return '';
  const ext = getExtensionFromUrl(url);
  // Use Google viewer for common document types to avoid forced downloads.
  // Works for: pdf, doc, docx, xls, xlsx, ppt, pptx, txt
  const supported = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt']);
  if (supported.has(ext)) {
    return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`;
  }
  return url;
};

const openPreviewInNewTab = (url) => {
  if (!url) return;
  const previewUrl = getPreviewUrl(url);
  window.open(previewUrl, '_blank', 'noopener,noreferrer');
};

function DepartmentDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const departmentId = Number(id);
  const [imageSize, setImageSize] = useState(null);

  const { data: departmentsRes } = useGetActiveDepartmentsQuery(undefined, {
    skip: Number.isNaN(departmentId),
  });

  const department = useMemo(() => {
    const list = departmentsRes?.data || departmentsRes || [];
    if (!Array.isArray(list)) return null;
    return list.find((d) => Number(d?.id) === departmentId) || null;
  }, [departmentsRes, departmentId]);

  const departmentHeroImageSrc = useMemo(
    () => departmentImageSrc(department?.image, { size: 'large' }),
    [department?.image]
  );

  const departmentThumbImageSrc = useMemo(
    () => departmentImageSrc(department?.image, { size: 'thumb' }),
    [department?.image]
  );

  const [largeImageLoaded, setLargeImageLoaded] = useState(false);

  useEffect(() => {
    setLargeImageLoaded(false);
  }, [departmentHeroImageSrc]);

  const descriptionHtml = useMemo(() => {
    const raw = department?.description;
    if (!raw) return '';
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(raw);
    if (hasHtmlTags) return raw;
    return raw.replace(/\n/g, '<br />');
  }, [department?.description]);

  const onImageLoad = useCallback((e) => {
    const img = e?.currentTarget;
    setLargeImageLoaded(true);
    if (!img) return;
    if (img.naturalWidth && img.naturalHeight) {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    }
  }, []);

  const fileName = useMemo(() => {
    const url = department?.upload_pdf?.url;
    if (!url) return '';
    return getFileNameFromUrl(url) || 'Attachment';
  }, [department?.upload_pdf?.url]);

  const canPreviewInline = useMemo(() => {
    const ext = getExtensionFromUrl(department?.upload_pdf?.url);
    return ext === 'pdf';
  }, [department?.upload_pdf?.url]);

  const docExt = useMemo(() => {
    return getExtensionFromUrl(department?.upload_pdf?.url);
  }, [department?.upload_pdf?.url]);

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#F7E5D5] px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="py-6">
          <button
            type="button"
            onClick={() => navigate('/landing')}
            className="mb-4 inline-flex items-center gap-1 rounded-lg py-1.5 text-sm font-medium text-gray-800 transition"
            aria-label="Back to landing page"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {department ? (
            <>
              <h2 className="mb-4 text-xl font-semibold text-gray-900">
                {department?.name || ''}
              </h2>

              <div className="mb-4 flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                    department?.status
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-200 text-gray-800'
                  }`}
                >
                  {department?.status ? 'Active' : 'Inactive'}
                </span>

                {department?.created_at ? (
                  <span className="inline-flex items-center rounded-full bg-[#D3A33D] px-3 py-1 text-xs font-semibold text-gray-900">
                    Created: {formatDate(department.created_at)}
                  </span>
                ) : null}

              </div>
              {descriptionHtml ? (
                <div
                  className="mb-4 text-sm text-gray-700 [&_a]:text-blue-700 [&_a]:underline [&_li]:ml-4 [&_li]:list-disc [&_ol]:ml-4 [&_ol]:list-decimal [&_p]:mb-2 [&_ul]:ml-4 [&_ul]:list-disc"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              ) : null}
              {departmentHeroImageSrc ? (
                <div className="mb-6 w-full rounded-2xl bg-white shadow-sm ring-1 ring-black/5 sm:w-2/3">
                  <div className="relative block h-[240px] w-full overflow-hidden rounded-2xl bg-gradient-to-b from-gray-50 to-gray-100 sm:h-[320px] lg:h-[380px]">
                    {departmentThumbImageSrc && !largeImageLoaded ? (
                      <img
                        src={departmentThumbImageSrc}
                        alt=""
                        aria-hidden
                        className="absolute inset-0 h-full w-full scale-105 rounded-xl object-cover object-center blur-sm"
                      />
                    ) : null}
                    <img
                      src={departmentHeroImageSrc}
                      alt={department?.name || 'Department'}
                      className={`absolute inset-0 h-full w-full rounded-xl bg-white object-cover object-center shadow-sm ring-1 ring-black/5 transition-opacity duration-500 ${
                        largeImageLoaded ? 'opacity-100' : 'opacity-0'
                      }`}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      onLoad={onImageLoad}
                    />
                  </div>
                </div>
              ) : null}

              {department?.drive_label ? (
                <div className="mt-6 w-full rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-black/5 sm:w-2/3">
                  <div className="mb-4 flex items-center gap-2 border-b border-gray-200 pb-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700 ring-1 ring-blue-200">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    </span>
                    <div className="flex flex-col">
                      <h3 className="text-base font-semibold text-gray-900">
                       Document File
                      </h3>
                    </div>
                  </div>

                  {department?.drive_link ? (
                    <a
                      href={department.drive_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open drive link in a new tab"
                      className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-blue-700 underline shadow-sm ring-1 ring-black/5 transition hover:bg-blue-50 hover:shadow-md"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      {department.drive_label}
                    </a>
                  ) : (
                    <span className="inline-flex items-center rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-black/5">
                      {department.drive_label}
                    </span>
                  )}
                </div>
              ) : null}

              {department?.upload_pdf?.url ? (
                <div className="mt-6 w-full rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-black/5 sm:w-2/3">
                  <div className="mb-4 flex items-center gap-2 border-b border-gray-200 pb-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#D3A33D]/20 text-[#8a6a1f] ring-1 ring-[#D3A33D]/40">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8" />
                        <path d="M8 17h8" />
                        <path d="M8 9h2" />
                      </svg>
                    </span>
                    <div className="flex flex-col">
                      <h3 className="text-base font-semibold text-gray-900">
                        Attached Document
                      </h3>
                      <p className="text-[11px] text-gray-600">
                        Preview or download the uploaded file
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-between">
                    <button
                      type="button"
                      onClick={() => openPreviewInNewTab(department.upload_pdf.url)}
                      className="flex min-w-0 flex-1 items-center gap-3 rounded-xl bg-white p-3 text-left shadow-sm ring-1 ring-black/5 transition hover:shadow-md"
                      title="Preview document in new tab"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 ring-1 ring-black/5">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-gray-800"
                          aria-hidden
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                          <path d="M8 13h8" />
                          <path d="M8 17h8" />
                          <path d="M8 9h2" />
                        </svg>
                      </div>

                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-gray-900">
                          {fileName || 'Attachment'}
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-600">
                          Click to preview in a new tab
                          {docExt ? ` • .${docExt}` : ''}
                          {canPreviewInline ? '' : ' (preview opens via viewer)'}
                        </div>
                      </div>
                    </button>

                    <div className="flex flex-wrap gap-2 sm:flex-col sm:justify-center">
                      <button
                        type="button"
                        onClick={() => openPreviewInNewTab(department.upload_pdf.url)}
                        className="inline-flex items-center justify-center rounded-lg bg-[#D3A33D] px-3 py-2 text-xs font-semibold text-gray-900 transition hover:opacity-90"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadFile(department.upload_pdf.url, fileName)}
                        className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white transition hover:opacity-90"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className="mb-4 text-sm text-gray-600">Loading department...</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DepartmentDetail;

